import { useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { useAuth } from '@/contexts/AuthContext';
import { useCrmLeads, createLead, updateLead, deleteLead, CrmLeadInput } from '@/hooks/useCrm';
import { CrmLead, LeadStage, ServiceLine, timestampToDate } from '@/types';
import { SERVICE_LINES, serviceLineLabel } from '@/lib/affiliate';

const STAGES: LeadStage[] = ['NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL', 'WON', 'LOST'];

const STAGE_LABELS: Record<LeadStage, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUALIFIED: 'Qualified',
  PROPOSAL: 'Proposal',
  WON: 'Won',
  LOST: 'Lost',
};

const STAGE_COLORS: Record<LeadStage, string> = {
  NEW: 'var(--text-2)',
  CONTACTED: '#6ea8fe',
  QUALIFIED: 'var(--accent)',
  PROPOSAL: '#c58af9',
  WON: 'var(--success)',
  LOST: 'var(--danger)',
};

const EMPTY_FORM: CrmLeadInput = {
  name: '',
  company: '',
  email: '',
  phone: '',
  source: '',
  affiliateCode: '',
  stage: 'NEW',
  value: undefined,
  serviceLine: undefined,
  notes: '',
};

const fmtUsd = (n?: number) =>
  typeof n === 'number' ? `$${n.toLocaleString('en-US')}` : '—';

function CRMContent() {
  const { currentUser } = useAuth();
  const { leads, loading } = useCrmLeads();

  const [stageFilter, setStageFilter] = useState<LeadStage | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<CrmLead | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CrmLeadInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const stats = useMemo(() => {
    const byStage = Object.fromEntries(STAGES.map((s) => [s, 0])) as Record<LeadStage, number>;
    let pipeline = 0;
    let won = 0;
    for (const l of leads) {
      byStage[l.stage] = (byStage[l.stage] || 0) + 1;
      if (l.stage === 'WON') won += l.value || 0;
      else if (l.stage !== 'LOST') pipeline += l.value || 0;
    }
    return { byStage, pipeline, won };
  }, [leads]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (stageFilter !== 'ALL' && l.stage !== stageFilter) return false;
      if (!s) return true;
      return (
        l.name.toLowerCase().includes(s) ||
        (l.company || '').toLowerCase().includes(s) ||
        (l.email || '').toLowerCase().includes(s) ||
        (l.source || '').toLowerCase().includes(s)
      );
    });
  }, [leads, stageFilter, search]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowForm(true);
  };

  const openEdit = (lead: CrmLead) => {
    setEditing(lead);
    setForm({
      name: lead.name,
      company: lead.company || '',
      email: lead.email || '',
      phone: lead.phone || '',
      source: lead.source || '',
      affiliateCode: lead.affiliateCode || '',
      stage: lead.stage,
      value: lead.value,
      serviceLine: lead.serviceLine,
      notes: lead.notes || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Drop empty-string optionals to keep documents clean.
      const payload: CrmLeadInput = {
        ...form,
        name: form.name.trim(),
        company: form.company?.trim() || undefined,
        email: form.email?.trim() || undefined,
        phone: form.phone?.trim() || undefined,
        source: form.source?.trim() || undefined,
        affiliateCode: form.affiliateCode?.trim() || undefined,
        notes: form.notes?.trim() || undefined,
        value: typeof form.value === 'number' && !Number.isNaN(form.value) ? form.value : undefined,
      };
      if (editing) {
        await updateLead(editing.id, payload);
      } else {
        await createLead(payload, currentUser!.uid);
      }
      setShowForm(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save lead');
    } finally {
      setSaving(false);
    }
  };

  const handleStageChange = async (lead: CrmLead, stage: LeadStage) => {
    try {
      await updateLead(lead.id, { stage });
    } catch (err) {
      console.error('Failed to update stage:', err);
    }
  };

  const handleDelete = async (lead: CrmLead) => {
    if (!window.confirm(`Delete lead "${lead.name}"? This cannot be undone.`)) return;
    try {
      await deleteLead(lead.id);
    } catch (err) {
      console.error('Failed to delete lead:', err);
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      {/* Header + stats */}
      <div className="terminal-card">
        <div className="card-header">
          <span className="card-title">CRM · Leads &amp; Deals</span>
          <div className="card-header-right">
            <button className="action-btn large" onClick={openAdd}>+ Add Lead</button>
          </div>
        </div>
        <div className="card-body">
          <div className="crm-stats">
            <div className="crm-stat">
              <span className="crm-stat__label">Leads</span>
              <span className="crm-stat__value">{leads.length}</span>
            </div>
            <div className="crm-stat">
              <span className="crm-stat__label">Open Pipeline</span>
              <span className="crm-stat__value">{fmtUsd(stats.pipeline)}</span>
            </div>
            <div className="crm-stat">
              <span className="crm-stat__label">Won</span>
              <span className="crm-stat__value" style={{ color: 'var(--success)' }}>{fmtUsd(stats.won)}</span>
            </div>
            {STAGES.map((s) => (
              <div className="crm-stat" key={s}>
                <span className="crm-stat__label" style={{ color: STAGE_COLORS[s] }}>{STAGE_LABELS[s]}</span>
                <span className="crm-stat__value">{stats.byStage[s]}</span>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="crm-toolbar">
            <div className="crm-filter-chips">
              <button
                className={`chip-btn${stageFilter === 'ALL' ? ' chip-btn--active' : ''}`}
                onClick={() => setStageFilter('ALL')}
              >
                All
              </button>
              {STAGES.map((s) => (
                <button
                  key={s}
                  className={`chip-btn${stageFilter === s ? ' chip-btn--active' : ''}`}
                  onClick={() => setStageFilter(s)}
                >
                  {STAGE_LABELS[s]}
                </button>
              ))}
            </div>
            <input
              className="terminal-input"
              placeholder="Search name, company, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: '280px' }}
            />
          </div>
        </div>
      </div>

      {/* Leads table */}
      <div className="terminal-card" style={{ marginTop: '14px' }}>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: 'var(--muted)', fontSize: '11px', letterSpacing: '0.12em' }}>
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--muted)', fontSize: '12px' }}>
              {leads.length === 0 ? 'No leads yet. Add your first lead to start tracking your pipeline.' : 'No leads match your filters.'}
            </div>
          ) : (
            <div className="table-wrap" style={{ border: 'none' }}>
              <table className="terminal-table table-readable">
                <thead>
                  <tr>
                    <th>NAME / COMPANY</th>
                    <th>CONTACT</th>
                    <th>SOURCE</th>
                    <th>SERVICE</th>
                    <th>STAGE</th>
                    <th style={{ textAlign: 'right' }}>VALUE</th>
                    <th style={{ textAlign: 'right' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead) => (
                    <tr key={lead.id}>
                      <td style={{ color: 'var(--text)' }}>
                        <div style={{ fontWeight: 700 }}>{lead.name}</div>
                        {lead.company && (
                          <div style={{ color: 'var(--muted)', fontSize: '11px' }}>{lead.company}</div>
                        )}
                      </td>
                      <td style={{ color: 'var(--muted)', fontSize: '12px' }}>
                        {lead.email && <div>{lead.email}</div>}
                        {lead.phone && <div>{lead.phone}</div>}
                        {!lead.email && !lead.phone && '—'}
                      </td>
                      <td className="cell-tight" style={{ color: 'var(--muted)' }}>
                        <div>{lead.affiliateCode || lead.source || '—'}</div>
                        {lead.consent && (
                          <div style={{ color: 'var(--success)', fontSize: '10px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            ✓ Consent on file
                          </div>
                        )}
                      </td>
                      <td className="cell-tight" style={{ color: 'var(--muted)' }}>
                        {lead.serviceLine ? serviceLineLabel(lead.serviceLine) : '—'}
                      </td>
                      <td className="cell-tight">
                        <select
                          className="terminal-select crm-stage-select"
                          value={lead.stage}
                          onChange={(e) => handleStageChange(lead, e.target.value as LeadStage)}
                          style={{ color: STAGE_COLORS[lead.stage], borderColor: STAGE_COLORS[lead.stage] }}
                        >
                          {STAGES.map((s) => (
                            <option key={s} value={s}>{STAGE_LABELS[s]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="cell-tight" style={{ textAlign: 'right', color: 'var(--text)', fontWeight: 700 }}>
                        {fmtUsd(lead.value)}
                      </td>
                      <td className="cell-tight" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="action-btn secondary crm-mini-btn" onClick={() => openEdit(lead)}>Edit</button>
                        <button className="action-btn danger crm-mini-btn" onClick={() => handleDelete(lead)}>Del</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div className="terminal-card" style={{ maxWidth: '520px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="card-header">
              <span className="card-title">{editing ? 'Edit Lead' : 'Add Lead'}</span>
            </div>
            <form className="card-body" onSubmit={handleSave}>
              {error && <div className="terminal-alert error">Error: {error}</div>}

              <div className="field-group">
                <label className="field-label">Name *</label>
                <input className="terminal-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Contact name" autoFocus />
              </div>

              <div className="crm-form-row">
                <div className="field-group">
                  <label className="field-label">Company</label>
                  <input className="terminal-input" value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company" />
                </div>
                <div className="field-group">
                  <label className="field-label">Stage</label>
                  <select className="terminal-select" value={form.stage}
                    onChange={(e) => setForm({ ...form, stage: e.target.value as LeadStage })}>
                    {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                  </select>
                </div>
              </div>

              <div className="crm-form-row">
                <div className="field-group">
                  <label className="field-label">Email</label>
                  <input className="terminal-input" type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
                </div>
                <div className="field-group">
                  <label className="field-label">Phone</label>
                  <input className="terminal-input" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" />
                </div>
              </div>

              <div className="crm-form-row">
                <div className="field-group">
                  <label className="field-label">Source</label>
                  <input className="terminal-input" value={form.source}
                    onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Web, X, referral…" />
                </div>
                <div className="field-group">
                  <label className="field-label">Deal Value (USD)</label>
                  <input className="terminal-input" type="number" min="0" value={form.value ?? ''}
                    onChange={(e) => setForm({ ...form, value: e.target.value === '' ? undefined : Number(e.target.value) })}
                    placeholder="0" />
                </div>
              </div>

              <div className="crm-form-row">
                <div className="field-group">
                  <label className="field-label">Service Line</label>
                  <select className="terminal-select" title="Service line" value={form.serviceLine ?? ''}
                    onChange={(e) => setForm({ ...form, serviceLine: (e.target.value || undefined) as ServiceLine | undefined })}>
                    <option value="">— Not set —</option>
                    {SERVICE_LINES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
                <div className="field-group">
                  <label className="field-label">Affiliate Code (optional)</label>
                  <input className="terminal-input" value={form.affiliateCode}
                    onChange={(e) => setForm({ ...form, affiliateCode: e.target.value })}
                    placeholder="If this lead came from an affiliate" />
                </div>
              </div>

              <div className="field-group">
                <label className="field-label">Notes</label>
                <textarea className="terminal-textarea" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes about this lead…" />
              </div>

              {editing && (
                <p style={{ fontSize: '9px', color: 'var(--muted)', letterSpacing: '0.06em', margin: 0 }}>
                  Added {timestampToDate(editing.createdAt)?.toLocaleDateString() || '—'}
                </p>
              )}

              <div className="btn-row">
                <button type="submit" className="action-btn large" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Lead'}
                </button>
                <button type="button" className="action-btn secondary" onClick={() => setShowForm(false)} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminCRMPage() {
  return (
    <ProtectedRoute requireAdmin>
      <Layout wide>
        <CRMContent />
      </Layout>
    </ProtectedRoute>
  );
}
